import { MaxUint256 } from '@ethersproject/constants';
import { parseEther } from '@ethersproject/units';
import { SpoolDto } from '../../../../../../../../types/spool-local';
import { useWeb3React } from '@web3-react/core';
import { Contract } from 'ethers';
import { isAddress } from 'ethers/lib/utils';
import { useContext } from 'react';
import { CheckboxTickIcon } from '../../../../../../../../assets/icons';
import Button from '../../../../../../../../components/Button';
import InfoPopUp from '../../../../../../../../components/InfoPopUp';
import TextInput from '../../../../../../../../components/TextInput';
import abis from '../../../../../../../../config/abis';
import {
    APPROVAL_PENDING,
    INCENTIVE_TOKEN_EQUALS_UNDERLYING_ASSET,
} from '../../../../../../../../config/constants';
import errorContext from '../../../../../../../../context/error/errorContext';
import modalContext from '../../../../../../../../context/modal/modalContext';
import useIsApproved from '../../../../../../../../hooks/web3/useIsApproved';
import useTokenDetails from '../../../../../../../../hooks/web3/useTokenDetails';
import { calculateMargin, preventOverflow } from '../../../../../../../../web3/utils';
import { IncentiveTokenType } from '../AddIncentivize';

type Props = {
    spoolDetails: SpoolDto;
    token: IncentiveTokenType;
    handleIncentiveTokenChange: (name: string, value: string) => void;
    isValidated: boolean;
    isValidToken: boolean;
};

const IncentiveTokens: React.FC<Props> = ({
    token,
    handleIncentiveTokenChange,
    spoolDetails,
    isValidated,
    isValidToken,
}) => {
    const { library } = useWeb3React();
    const { setError } = useContext(errorContext);

    const { openModal, closeModal, setModalData } = useContext(modalContext);

    const tokenDetails = useTokenDetails(
        isAddress(token.address) ? token.address : undefined
    );

    const { isApproved, mutate } = useIsApproved(
        token.address,
        spoolDetails.address,
        token.amount
    );

    const { asset } = spoolDetails;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        handleIncentiveTokenChange(
            name,
            name === 'amount' ? preventOverflow(value, tokenDetails.decimals) : value
        );
    };

    const approve = async () => {
        try {
            if (!library) return;

            if (asset.id.toLowerCase() === token.address.toLowerCase()) {
                openModal('errorModal');
                setModalData((prev) => ({
                    ...prev,
                    message: INCENTIVE_TOKEN_EQUALS_UNDERLYING_ASSET,
                }));
                return;
            }

            setModalData((prev) => ({ ...prev, message: APPROVAL_PENDING }));
            openModal('waitingModal');

            const contract = new Contract(token.address, abis.erc20, library.getSigner());

            let useExact = false;

            const estimatedGas = await contract.estimateGas
                .approve(spoolDetails.address, MaxUint256)
                .catch(() => {
                    // general fallback for tokens who restrict approval amounts
                    useExact = true;

                    return contract.estimateGas.approve(
                        spoolDetails.address,
                        parseEther(token.amount)
                    );
                });

            const approve = await contract.approve(
                spoolDetails.address,
                useExact ? parseEther(token.amount) : MaxUint256,
                {
                    gasLimit: calculateMargin(estimatedGas),
                }
            );

            await approve.wait();

            await mutate();
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <>
            <div className='advanced-field-container'>
                <div className='field'>
                    <div className='title'>Your token's Address</div>
                    <input
                        type='text'
                        name='address'
                        value={token?.address}
                        onChange={handleChange}
                    />
                </div>
                <div className='field'>
                    <div className='title'>
                        Number of incentive tokens you will deposit
                    </div>
                    <TextInput
                        name='amount'
                        value={token?.amount}
                        onChange={handleChange}
                    />
                </div>
                <div className='field'>
                    <div className='title'>
                        <span>Number of days your tokens should be emitted?</span>
                        <InfoPopUp>
                            <span>
                                Tokens will be emitted equally per block over the number
                                of days you select
                            </span>
                        </InfoPopUp>
                    </div>
                    <TextInput name='rate' value={token?.rate} onChange={handleChange} />
                </div>
                <div className='field button-container'>
                    {isApproved ? (
                        <div className='approved'>
                            <CheckboxTickIcon />
                            <span>{tokenDetails.symbol} approved for Deposit</span>
                        </div>
                    ) : (
                        <Button
                            disabled={!isValidated || isApproved}
                            variant='secondary'
                            onClick={approve}
                        >
                            {tokenDetails.isLoading
                                ? 'Verifying token address...'
                                : !isValidated && !isValidToken && token.address !== ''
                                ? 'Invalid  Token'
                                : `Approve Your ${tokenDetails.symbol ?? 'Tokens'}`}
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
};

export default IncentiveTokens;
